import { useEffect, useState } from 'react';
import { Link, Navigate } from 'react-router-dom';
import { useMsal } from '@azure/msal-react';
import { createPortalApi } from '../../api/portalApi';
import { useRuntimeConfig } from '../../context/RuntimeConfigContext';
import { useUserRole } from '../../context/UserRoleContext';
import { useToast } from '../../components/Toast';
import Field from '../../components/settings/Field';
import ModelSelect from '../../components/settings/ModelSelect';
import type { OrgSettings } from '../../types';

const EMPTY: OrgSettings = { targetType: 'jira' };

export default function LlmSettingsPage() {
  const { instance } = useMsal();
  const cfg           = useRuntimeConfig();
  const { isAdmin }   = useUserRole();
  const api            = createPortalApi(instance, cfg.msalClientId, cfg.apiBaseUrl);
  const { showToast }  = useToast();

  const [settings, setSettings] = useState<OrgSettings>(EMPTY);
  const [loading, setLoading]   = useState(true);
  const [saving, setSaving]     = useState(false);
  const [error, setError]       = useState('');

  useEffect(() => {
    if (!isAdmin) return;
    api.getSettings()
      .then(s => setSettings(s ?? EMPTY))
      .catch(e => setError(e.message))
      .finally(() => setLoading(false));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAdmin]);

  if (!isAdmin) return <Navigate to="/settings/profile" replace />;

  function set<K extends keyof OrgSettings>(key: K, value: OrgSettings[K]) {
    setSettings(s => ({ ...s, [key]: value }));
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setSaving(true);
    try {
      await api.saveSettings(settings);
      if (settings.llmProvider && settings.llmApiKey) {
        localStorage.setItem('fint_llm_config', JSON.stringify({
          llmProvider:      settings.llmProvider,
          llmModel:         settings.llmModel         ?? '',
          llmApiKey:        settings.llmApiKey,
          llmAzureEndpoint: settings.llmAzureEndpoint ?? '',
        }));
      } else {
        localStorage.removeItem('fint_llm_config');
      }
      showToast('Settings saved successfully.', 'success');
    } catch (e: any) {
      setError(e.message);
    } finally {
      setSaving(false);
    }
  }

  if (loading) return <div className="page-loading"><span className="spinner" />Loading settings…</div>;

  return (
    <div>
      <Link to="/settings" className="settings-back-link">← Organisation Settings</Link>

      {error && <div className="alert alert-error">{error}</div>}

      <form className="settings-form" onSubmit={handleSubmit}>
        <div className="settings-card">
          <h2 className="settings-section-title">Global AI / LLM Configuration</h2>
          <p className="settings-section-desc">
            Organisation-wide AI key used by the Chrome extension and portal API Testing tab.
            Users can override this with their own key under <strong>My Settings</strong>.
          </p>
          <div className="form-grid">
            <div className="form-group">
              <label>LLM Provider</label>
              <select
                className="form-input"
                value={settings.llmProvider ?? 'claude'}
                onChange={e => set('llmProvider', e.target.value)}
              >
                <option value="claude">Claude (Anthropic)</option>
                <option value="openai">OpenAI</option>
                <option value="groq">Groq</option>
                <option value="azureopenai">Azure OpenAI</option>
              </select>
            </div>
            {settings.llmProvider === 'azureopenai' ? (
              <Field
                label="Deployment Name"
                placeholder="e.g. my-gpt4-deployment"
                value={settings.llmModel ?? ''}
                onChange={v => set('llmModel', v)}
              />
            ) : (
              <ModelSelect
                provider={settings.llmProvider ?? 'claude'}
                value={settings.llmModel ?? ''}
                onChange={v => set('llmModel', v)}
              />
            )}
            <Field label="API Key" placeholder="Provider API key" value={settings.llmApiKey ?? ''} onChange={v => set('llmApiKey', v)} secret />
            {settings.llmProvider === 'azureopenai' && (
              <Field
                label="Azure Endpoint"
                placeholder="https://my-resource.openai.azure.com"
                value={settings.llmAzureEndpoint ?? ''}
                onChange={v => set('llmAzureEndpoint', v)}
              />
            )}
          </div>
        </div>

        <div className="settings-card">
          <h2 className="settings-section-title">Duplicate Detection (Embeddings)</h2>
          <p className="settings-section-desc">
            Enables semantic similarity search on the Approved Catalog. Requires an Azure OpenAI embedding deployment
            (<code>text-embedding-3-small</code> recommended).
          </p>
          <div className="form-grid">
            <Field label="Azure OpenAI Endpoint"  placeholder="https://<resource>.openai.azure.com" value={settings.embeddingEndpoint   ?? ''} onChange={v => set('embeddingEndpoint', v)} />
            <Field label="Embedding API Key"      placeholder="Azure OpenAI API key"                value={settings.embeddingApiKey     ?? ''} onChange={v => set('embeddingApiKey', v)} secret />
            <Field label="Deployment Name"        placeholder="text-embedding-3-small"             value={settings.embeddingDeployment ?? ''} onChange={v => set('embeddingDeployment', v)} />
          </div>
        </div>

        <div className="settings-card">
          <h2 className="settings-section-title">
            Azure AI Search <span className="hint-text" style={{ fontWeight: 400 }}>(optional — for catalogs with 1000+ entries)</span>
          </h2>
          <p className="settings-section-desc">
            When configured, duplicate detection queries the search index instead of comparing vectors in memory.
            Leave blank to use in-memory comparison.
          </p>
          <div className="form-grid">
            <Field label="Search Endpoint" placeholder="https://<resource>.search.windows.net" value={settings.aiSearchEndpoint  ?? ''} onChange={v => set('aiSearchEndpoint', v)} />
            <Field label="Search API Key"  placeholder="Azure AI Search admin or query key"    value={settings.aiSearchApiKey    ?? ''} onChange={v => set('aiSearchApiKey', v)} secret />
            <Field label="Index Name"      placeholder="approved-catalog"                       value={settings.aiSearchIndexName ?? ''} onChange={v => set('aiSearchIndexName', v)} />
          </div>
        </div>

        <div className="settings-footer">
          <button type="submit" className="btn btn-primary" disabled={saving}>
            {saving ? 'Saving…' : 'Save Settings'}
          </button>
        </div>
      </form>
    </div>
  );
}
